import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, finalize, firstValueFrom, from, shareReplay } from 'rxjs';
import { AuthenticationRequest, AzureUserLoginDTO, UserLoginAuthResponseDTO, UserLoginCreateRequestDTO, UserLoginDeleteRequestDTO, UserLoginDTO, UserLoginReadByUserIdRequestDTO, UserLoginResponseDTO, UserLoginUpdateRequestDTO, UserLoginValidateUserNameRequestDTO } from './userlogin.interface';
import { UserLogin_auth, UserLogin_AzureAuth, UserLogin_create, UserLogin_delete, UserLogin_logout, UserLogin_readByUserId, UserLogin_refresh, UserLogin_update, UserLogin_validateToken, UserLogin_validateUserName } from 'src/app/GlobalVariables';
import { query } from '@angular/animations';
import { ApiConfigurationService } from '../ApiConfigurationService/api-configuration.service';
import { PushNotificationService } from '../PushNotificationService/pushnotification.service';
import { resolveTenantUrl, tenantOverrideOptions } from '../ApiConfigurationService/resolve-tenant-url.util';


// Marks the refresh/logout calls themselves (and a request already retried once) so the
// ValidationInterceptor never tries to refresh on their failure.
export const SKIP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root'
})
export class UserLoginService {
  private readonly _userSubject = new BehaviorSubject<UserLoginResponseDTO | null>(this.loadUserFromStorage());
  readonly user$ = this._userSubject.asObservable();
  private readonly apiConfigurationService = inject(ApiConfigurationService);
  // Resolved lazily via Injector, NOT a normal constructor/field dependency: PushNotificationService
  // itself injects UserLoginService (to read the current user), so injecting it back here directly
  // would be a circular DI dependency between two root-provided singletons. Injector.get() defers
  // resolution until SignOutUser() actually runs, by which point both services already exist.
  private readonly injector = inject(Injector);

  constructor(private http: HttpClient, private router: Router) { }

  private refreshInFlight$: Observable<string> | null = null;

  /**
   * Swaps the stored refresh token for a new access token (+ rotated refresh token).
   * Single-flight within the tab, and serialized across tabs with a Web Lock so two tabs
   * never spend the same refresh token: the second one finds the access token already
   * changed and just reuses it. Errors out when there is no refresh token or the server
   * rejects it (a 401 there already signs the user out in the interceptor).
   */
  refreshAccessToken(failedToken: string): Observable<string> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = from(this.refreshUnderLock(failedToken)).pipe(
        finalize(() => this.refreshInFlight$ = null),
        shareReplay(1)
      );
    }
    return this.refreshInFlight$;
  }

  private refreshUnderLock(failedToken: string): Promise<string> {
    const run = async (): Promise<string> => {
      const current = localStorage.getItem('access_token');
      if (current && current !== failedToken) return current;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token');

      const res = await firstValueFrom(this.http.post<UserLoginAuthResponseDTO>(
        UserLogin_refresh, { refreshToken }, { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }));
      const token = res.data?.token?.trim();
      if (!token) throw new Error('Refresh returned no token');

      localStorage.setItem('access_token', token);
      if (res.data.refreshToken) localStorage.setItem('refresh_token', res.data.refreshToken);
      return token;
    };
    return navigator.locks ? navigator.locks.request('adverp-refresh', run) : run();
  }
  isAuthenticated() {
    if (localStorage.getItem('access_token') != null || localStorage.getItem('access_token') != undefined) {
      return true;
    } else {
      return false
    }
  }

  SignOutUser() {
    // Idempotent: an expired token makes several independent callers fail at
    // different times (validateToken -> Home.Logout, RouteGuard's permission
    // read, the 401 interceptor) and each calls this. The first one signs out
    // and sends the user to /login, where login.component starts loading the
    // tenant's ApiConfiguration; a LATER caller used to run localStorage.clear()
    // + clearConfiguration() again, wiping the config the login page had just
    // loaded (and its CompanyId/BranchId), so the form was visible but /auth
    // was blocked with "Tenant gateway configuration is not loaded" until a
    // page refresh. No token == already signed out: nothing to do.
    if (!localStorage.getItem('access_token')) {
      // Still make sure the user isn't left on a protected page, but never
      // touch storage/config here (see above).
      if (!this.router.url.startsWith('/login')) this.router.navigate(['/login']);
      return;
    }

    // Deregister this browser's push-notification device for whoever's signing out, BEFORE the
    // session is cleared below (User() reads from localStorage, so this has to run first). See
    // PushNotificationService.unregisterCurrentDevice() for why this matters: without it, a
    // notification already queued for this user could still get delivered to whoever logs into
    // this same browser next.
    try {
      this.injector.get(PushNotificationService).unregisterCurrentDevice();
    } catch (err) {
      console.warn('[UserLoginService] Could not unregister push device on sign-out:', err);
    }

    // Best effort: revoke this login's refresh token server-side. Fired before the clear below so the
    // interceptor still reads this session's tenant headers; a failure changes nothing for the user.
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      this.http.post(UserLogin_logout, { refreshToken }, { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }).subscribe({ error: () => { } });
    }

    // Device-level UI preferences (not session data) must survive the wipe: the menu collapsed/expanded choice (Home shell).
    const keepOnSignOut: Record<string, string> = {};
    for (const key of ['cl_menu_collapsed']) {
      const value = localStorage.getItem(key);
      if (value !== null) keepOnSignOut[key] = value;
    }
    localStorage.clear()
    Object.keys(keepOnSignOut).forEach(key => localStorage.setItem(key, keepOnSignOut[key]));
    // localStorage.clear() drops the cached override key, but this service
    // (and ApiConfigurationService) are root singletons that survive an
    // in-SPA logout/login cycle, so the in-memory overrides must be reset
    // explicitly too — otherwise the next tenant to log in on this tab would
    // briefly inherit the previous tenant's cached gateway URLs.
    this.apiConfigurationService.clearConfiguration()
    const host = window.location.host;
    this.router.navigate(['/login'])
    // if(this.User() && this.User().companyId > 0){
    //   this.router.navigate(['/login'],{queryParams:{companyId: this.User().companyId}})
    // }else{
    //   this.router.navigate(['/login'])
    // }
  }


  UserLogin_auth(data: AuthenticationRequest): Observable<UserLoginAuthResponseDTO> {
    return this.http.post<UserLoginAuthResponseDTO>(UserLogin_auth, data);
  }

  UserLogin_validateToken(): Observable<UserLoginResponseDTO> {
    return this.http.get<UserLoginResponseDTO>(UserLogin_validateToken);
  }

  UserLogin_create(data: UserLoginCreateRequestDTO): Observable<UserLoginDTO> {
    return this.http.post<UserLoginDTO>(UserLogin_create, data);
  }

  UserLogin_update(data: UserLoginUpdateRequestDTO): Observable<UserLoginDTO> {
    return this.http.post<UserLoginDTO>(UserLogin_update, data);
  }

  UserLogin_delete(data: UserLoginDeleteRequestDTO): Observable<void> {
    return this.http.post<void>(UserLogin_delete, data);
  }

  // baseUrlOverride (optional, both methods): the viewed tenant's own AM
  // gateway origin, e.g. CompanyDetailComponent.apiConfigOverride.AM
  // (company-users tab). Omitted for every other caller - behaves exactly
  // as before.
  UserLogin_readByUserId(data: UserLoginReadByUserIdRequestDTO, baseUrlOverride?: string, companyId?: string | number, branchId?: string | number): Observable<UserLoginDTO> {
    return this.http.post<any>(resolveTenantUrl(UserLogin_readByUserId, baseUrlOverride), data, tenantOverrideOptions(baseUrlOverride, companyId ?? 0, branchId ?? 0));
  }

  UserLogin_validateUserName(data: UserLoginValidateUserNameRequestDTO, baseUrlOverride?: string, companyId?: string | number, branchId?: string | number): Observable<any> {
    return this.http.post<any>(resolveTenantUrl(UserLogin_validateUserName, baseUrlOverride), data, tenantOverrideOptions(baseUrlOverride, companyId ?? 0, branchId ?? 0));
  }

  UserLogin_AzureAuth(data: AzureUserLoginDTO): Observable<any> {
    return this.http.post<any>(UserLogin_AzureAuth, data);
  }


  UserData = {}

  // private userObject = new BehaviorSubject<any>({});

  // setData(value: any) {
  //   this.userObject.next(value);
  // }

  saveUserData(data: UserLoginResponseDTO) {
    localStorage.setItem('userObject', encodeURIComponent(JSON.stringify(data)));
    this._userSubject.next(data);
  }

  User(): UserLoginResponseDTO {
    let userObject: any = localStorage.getItem('userObject')
    this.UserData = JSON.parse(decodeURIComponent(userObject))
    return JSON.parse(decodeURIComponent(userObject))
  }

  private loadUserFromStorage(): UserLoginResponseDTO | null {
    try {
      const raw = localStorage.getItem('userObject');
      if (!raw) return null;
      return JSON.parse(decodeURIComponent(raw));
    } catch {
      return null;
    }
  }

  getCurrentUser(): UserLoginResponseDTO | null {
    return this._userSubject.getValue();
  }

  authorizedPages: string[] = []

  isUserAdminOrSupervisor(): boolean {
    let roles = this.User().roleId.split(",").map(function (item) {
      return item.trim();
    })
    if (roles.includes('2') || roles.includes('3')) {
      return true
    } else {
      return false
    }
  }


  isAdmin(): boolean {
    let roles = this.User().roleId.split(",").map(function (item) {
      return item.trim();
    })
    if (roles.includes('1')) {
      return true
    } else {
      return false
    }
  }

}
